// Service Worker Registration and Management
// Utilities for registering and managing the service worker

/**
 * Service Worker Registration Manager
 */
class ServiceWorkerManager {
  constructor() {
    this.registration = null;
    this.isUpdateAvailable = false;
    this.callbacks = {
      onRegistered: [],
      onUpdateAvailable: [],
      onOffline: [],
      onOnline: [],
    };

    // Bind methods
    this.handleOnline = this.handleOnline.bind(this);
    this.handleOffline = this.handleOffline.bind(this);
  }

  /**
   * Register the service worker
   */
  async register() {
    if (!('serviceWorker' in navigator)) {
      console.warn('Service Worker not supported in this browser');
      return false;
    }

    try {
      console.log('[SW Manager] Registering service worker...');

      this.registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
      });

      console.log('[SW Manager] Service worker registered successfully');

      // Handle different service worker states
      if (this.registration.installing) {
        console.log('[SW Manager] Service worker installing...');
        this.handleInstalling(this.registration.installing);
      } else if (this.registration.waiting) {
        console.log('[SW Manager] Service worker waiting...');
        this.handleWaiting(this.registration.waiting);
      } else if (this.registration.active) {
        console.log('[SW Manager] Service worker active');
        this.handleActive(this.registration.active);
      }

      // Listen for updates
      this.registration.addEventListener('updatefound', () => {
        console.log('[SW Manager] Service worker update found');
        this.handleUpdateFound();
      });

      // Listen for online/offline events
      window.addEventListener('online', this.handleOnline);
      window.addEventListener('offline', this.handleOffline);

      // Notify registration complete
      this.trigger('onRegistered', this.registration);

      return true;
    } catch (error) {
      console.error('[SW Manager] Service worker registration failed:', error);
      return false;
    }
  }

  /**
   * Handle service worker installing state
   */
  handleInstalling(worker) {
    worker.addEventListener('statechange', () => {
      if (worker.state === 'installed') {
        console.log('[SW Manager] Service worker installed');
      }
    });
  }

  /**
   * Handle service worker waiting state
   */
  handleWaiting(worker) {
    console.log('[SW Manager] New service worker waiting to activate');
    this.isUpdateAvailable = true;
    this.trigger('onUpdateAvailable', worker);
  }

  /**
   * Handle service worker active state
   */
  handleActive(_worker) {
    console.log('[SW Manager] Service worker is active and controlling the page');
  }

  /**
   * Handle update found event
   */
  handleUpdateFound() {
    const installingWorker = this.registration.installing;

    installingWorker.addEventListener('statechange', () => {
      if (installingWorker.state === 'installed') {
        if (navigator.serviceWorker.controller) {
          // New update available
          console.log('[SW Manager] New content is available; please refresh');
          this.isUpdateAvailable = true;
          this.trigger('onUpdateAvailable', installingWorker);
        } else {
          // Content is cached for the first time
          console.log('[SW Manager] Content is cached for offline use');
        }
      }
    });
  }

  /**
   * Handle online event
   */
  handleOnline() {
    console.log('[SW Manager] App is back online');
    this.trigger('onOnline');
  }

  /**
   * Handle offline event
   */
  handleOffline() {
    console.log('[SW Manager] App is offline');
    this.trigger('onOffline');
  }

  /**
   * Apply available update with subscription preservation
   */
  async applyUpdate() {
    if (this.registration && this.registration.waiting) {
      console.log('[SW Manager] Applying update with subscription preservation');

      // Send message to waiting service worker to skip waiting
      // The service worker will preserve subscriptions before skipping
      this.registration.waiting.postMessage({ type: 'SKIP_WAITING' });

      // Wait a bit for the service worker to activate
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Reload the page to activate the new service worker
      window.location.reload();
    } else {
      console.warn('[SW Manager] No waiting service worker to update');
    }
  }

  /**
   * Skip waiting for service worker update
   */
  async skipWaiting() {
    if (this.registration && this.registration.waiting) {
      console.log('[SW Manager] Skipping waiting for service worker update');
      this.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
  }

  /**
   * Check if app is running offline
   */
  isOffline() {
    return !navigator.onLine;
  }

  /**
   * Get service worker version
   */
  async getVersion() {
    if (!this.registration || !this.registration.active) {
      return null;
    }

    return new Promise((resolve) => {
      const messageChannel = new MessageChannel();
      messageChannel.port1.onmessage = (event) => {
        resolve(event.data.version);
      };

      this.registration.active.postMessage({ type: 'GET_VERSION' }, [messageChannel.port2]);
    });
  }

  /**
   * Cache specific URLs
   */
  cacheUrls(urls) {
    if (this.registration && this.registration.active) {
      this.registration.active.postMessage({
        type: 'CACHE_URLS',
        payload: { urls },
      });
    }
  }

  /**
   * Manually cache critical data for offline use
   * This allows the app to proactively cache important data
   */
  async cacheCriticalData() {
    if (!this.registration || !navigator.serviceWorker.controller) {
      console.warn('[SW Manager] Service worker not ready for data caching');
      return false;
    }

    try {
      // Send message to service worker to cache critical endpoints
      const criticalUrls = [
        '/auth/me',
        '/training-sessions/',
        '/availability/',
        '/crews/',
        '/training-groups/',
      ];

      await this.sendMessage({
        type: 'CACHE_CRITICAL_DATA',
        payload: { urls: criticalUrls },
      });

      console.log('[SW Manager] Critical data caching initiated');
      return true;
    } catch (error) {
      console.error('[SW Manager] Failed to initiate critical data caching:', error);
      return false;
    }
  }

  /**
   * Cache specific month's training data
   */
  async cacheMonthData(year, month) {
    if (!this.registration || !navigator.serviceWorker.controller) {
      console.warn('[SW Manager] Service worker not ready for month data caching');
      return false;
    }

    try {
      // Calculate date range for the month
      const startDate = new Date(year, month - 1, 1).toISOString().split('T')[0];
      const endDate = new Date(year, month, 0).toISOString().split('T')[0];

      const monthUrls = [
        `/training-sessions/?start_date=${startDate}&end_date=${endDate}`,
        `/availability/?start_date=${startDate}&end_date=${endDate}`,
        `/planning/daily-data?date=${startDate}`, // Will cache multiple days as needed
      ];

      await this.sendMessage({
        type: 'CACHE_MONTH_DATA',
        payload: { urls: monthUrls, year, month },
      });

      console.log(`[SW Manager] Month data caching initiated for ${year}-${month}`);
      return true;
    } catch (error) {
      console.error('[SW Manager] Failed to initiate month data caching:', error);
      return false;
    }
  }

  /**
   * Clear expired cache data
   */
  async clearExpiredData() {
    if (!this.registration || !navigator.serviceWorker.controller) {
      console.warn('[SW Manager] Service worker not ready for cache cleanup');
      return false;
    }

    try {
      await this.sendMessage({
        type: 'CLEAR_EXPIRED_DATA',
      });

      console.log('[SW Manager] Cache cleanup initiated');
      return true;
    } catch (error) {
      console.error('[SW Manager] Failed to initiate cache cleanup:', error);
      return false;
    }
  }

  /**
   * Get cache status and statistics
   */
  async getCacheStatus() {
    if (!this.registration || !navigator.serviceWorker.controller) {
      return null;
    }

    try {
      const response = await this.sendMessage({
        type: 'GET_CACHE_STATUS',
      });

      return response;
    } catch (error) {
      console.error('[SW Manager] Failed to get cache status:', error);
      return null;
    }
  }

  /**
   * Send message to service worker with response handling
   */
  async sendMessage(message) {
    if (!navigator.serviceWorker.controller) {
      throw new Error('No service worker controller available');
    }

    return new Promise((resolve, reject) => {
      const channel = new MessageChannel();

      channel.port1.onmessage = (event) => {
        if (event.data.error) {
          reject(new Error(event.data.error));
        } else {
          resolve(event.data);
        }
      };

      navigator.serviceWorker.controller.postMessage(message, [channel.port2]);

      // Timeout after 10 seconds
      setTimeout(() => {
        reject(new Error('Service worker message timeout'));
      }, 10000);
    });
  }

  /**
   * Add event listener
   */
  on(event, callback) {
    if (this.callbacks[event]) {
      this.callbacks[event].push(callback);
    }
  }

  /**
   * Remove event listener
   */
  off(event, callback) {
    if (this.callbacks[event]) {
      const index = this.callbacks[event].indexOf(callback);
      if (index > -1) {
        this.callbacks[event].splice(index, 1);
      }
    }
  }

  /**
   * Trigger event callbacks
   */
  trigger(event, data) {
    if (this.callbacks[event]) {
      this.callbacks[event].forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          console.error(`[SW Manager] Error in ${event} callback:`, error);
        }
      });
    }
  }

  /**
   * Unregister service worker (for development/testing)
   */
  async unregister() {
    if (this.registration) {
      const result = await this.registration.unregister();
      console.log('[SW Manager] Service worker unregistered:', result);
      return result;
    }
    return false;
  }
}

// Create singleton instance
const swManager = new ServiceWorkerManager();

// Export for use in React components
export default swManager;
